import { useState } from "react";
import { Loader2, X, Upload, Package, CheckCircle2 } from "lucide-react";
import * as XLSX from "xlsx";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_BADGE = {
  ready: { label: "Ready to Import", cls: "bg-emerald-100 text-emerald-700" },
  exists: { label: "Already Exists", cls: "bg-amber-100 text-amber-700" },
  brand_not_found: { label: "Brand Not Found", cls: "bg-orange-100 text-orange-700" },
  invalid: { label: "Invalid", cls: "bg-red-100 text-red-700" },
};

const norm = (s) => String(s == null ? "" : s).toLowerCase().replace(/[.\s_]/g, "");
const BRAND_KEYS = ["brand", "brandname", "company"];
const NAME_KEYS = ["productname", "product", "particulars", "item", "itemname", "name"];

function findCol(headerRow, keys) {
  for (let i = 0; i < headerRow.length; i++) if (keys.includes(norm(headerRow[i]))) return i;
  return -1;
}

export default function ImportProductsModal({ onClose, onImported }) {
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [progress, setProgress] = useState(null);
  const [filter, setFilter] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError(""); setSummary(null); setRows(null); setFilter(null); setParsing(true); setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", blankrows: false });

      let headerIdx = -1, brandCol = -1, nameCol = -1;
      for (let i = 0; i < aoa.length; i++) {
        const bc = findCol(aoa[i], BRAND_KEYS);
        const nc = findCol(aoa[i], NAME_KEYS);
        if (bc !== -1 && nc !== -1) { headerIdx = i; brandCol = bc; nameCol = nc; break; }
      }
      if (headerIdx === -1) {
        setError("Could not find 'Brand' and 'Product Name' columns in the sheet. Please check the file.");
        setParsing(false);
        return;
      }
      const raw = [];
      for (let i = headerIdx + 1; i < aoa.length; i++) {
        const row = aoa[i];
        const brand = String(row[brandCol] ?? "").trim();
        const name = String(row[nameCol] ?? "").trim();
        if (!brand && !name) continue;
        raw.push({ brand, name });
      }
      if (raw.length === 0) {
        setError("Found the header row but there are no data rows below it.");
        setParsing(false);
        return;
      }
      const { data } = await api.post("/products/import/preview", { rows: raw });
      setRows(data.rows);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not read the Excel file. Please upload a valid .xlsx file.");
    } finally { setParsing(false); }
  };

  const confirmImport = async () => {
    if (!rows) return;
    setImporting(true); setError("");
    const ready = rows.filter((r) => r.status === "ready").map((r) => ({ brand: r.brand, name: r.name }));
    const acc = {
      total: rows.length, imported: 0,
      existing: rows.filter((r) => r.status === "exists").length,
      brand_not_found: rows.filter((r) => r.status === "brand_not_found").length,
      invalid: rows.filter((r) => r.status === "invalid").length,
    };
    const CHUNK = 50;
    setProgress({ done: 0, total: ready.length });
    try {
      for (let i = 0; i < ready.length; i += CHUNK) {
        const batch = ready.slice(i, i + CHUNK);
        const { data } = await api.post("/products/import", { rows: batch });
        acc.imported += data.imported;
        acc.existing += data.existing;
        setProgress({ done: Math.min(i + CHUNK, ready.length), total: ready.length });
      }
      setSummary(acc);
      onImported && onImported();
    } catch (err) {
      setError((formatApiErrorDetail(err.response?.data?.detail) || "Import interrupted") + `. Imported ${acc.imported} so far — re-upload the same file to finish the rest (existing products are skipped).`);
    } finally { setImporting(false); }
  };

  const counts = rows ? rows.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {}) : {};
  const visibleRows = rows ? (filter ? rows.filter((r) => r.status === filter) : rows) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div data-testid="import-products-modal" className="relative flex max-h-[88vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-100 p-5">
          <div>
            <h3 className="text-lg font-bold text-slate-800">Import Products from Excel</h3>
            <p className="text-xs text-slate-500">Sheet needs only <b>Brand</b> and <b>Product Name</b> columns. Prices, images, unit rates & offers are added manually later.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        <div className="overflow-y-auto p-5">
          {!summary && (
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/50 px-4 py-6 text-sm font-semibold text-blue-700 hover:bg-blue-50">
              {parsing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
              {parsing ? "Reading file..." : fileName ? `Change file (${fileName})` : "Choose .xlsx file"}
              <input data-testid="import-products-file-input" type="file" accept=".xlsx,.xls" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleFile(f); }} />
            </label>
          )}

          {error && <p data-testid="import-products-error" className="mt-3 text-sm font-medium text-red-600">{error}</p>}

          {summary && (
            <div data-testid="import-products-summary" className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-500" />
              <h4 className="text-lg font-bold text-slate-800">Import complete</h4>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                <div className="rounded-lg bg-slate-100 px-3 py-2"><p className="text-xl font-extrabold text-slate-800" data-testid="psummary-total">{summary.total}</p><p className="text-[11px] text-slate-500">Total Rows</p></div>
                <div className="rounded-lg bg-emerald-50 px-3 py-2"><p className="text-xl font-extrabold text-emerald-700" data-testid="psummary-imported">{summary.imported}</p><p className="text-[11px] text-slate-500">Imported</p></div>
                <div className="rounded-lg bg-amber-50 px-3 py-2"><p className="text-xl font-extrabold text-amber-700" data-testid="psummary-existing">{summary.existing}</p><p className="text-[11px] text-slate-500">Already Existing</p></div>
                <div className="rounded-lg bg-orange-50 px-3 py-2"><p className="text-xl font-extrabold text-orange-700" data-testid="psummary-brand">{summary.brand_not_found}</p><p className="text-[11px] text-slate-500">Brand Not Found</p></div>
                <div className="rounded-lg bg-red-50 px-3 py-2"><p className="text-xl font-extrabold text-red-700" data-testid="psummary-invalid">{summary.invalid}</p><p className="text-[11px] text-slate-500">Invalid/Skipped</p></div>
              </div>
              <button data-testid="import-products-done-button" onClick={onClose} className="mt-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">Done</button>
            </div>
          )}

          {rows && !summary && (
            <>
              <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                {[
                  { key: "ready", label: "Ready", count: counts.ready || 0, base: "bg-emerald-100 text-emerald-700", active: "ring-2 ring-emerald-500 ring-offset-1" },
                  { key: "exists", label: "Already Exists", count: counts.exists || 0, base: "bg-amber-100 text-amber-700", active: "ring-2 ring-amber-500 ring-offset-1" },
                  { key: "brand_not_found", label: "Brand Not Found", count: counts.brand_not_found || 0, base: "bg-orange-100 text-orange-700", active: "ring-2 ring-orange-500 ring-offset-1" },
                  { key: "invalid", label: "Invalid", count: counts.invalid || 0, base: "bg-red-100 text-red-700", active: "ring-2 ring-red-500 ring-offset-1" },
                ].map((b) => (
                  <button
                    key={b.key}
                    type="button"
                    data-testid={`pfilter-${b.key}`}
                    onClick={() => setFilter((f) => (f === b.key ? null : b.key))}
                    className={`rounded-full px-2.5 py-1 transition ${b.base} ${filter === b.key ? b.active : "hover:opacity-80"}`}
                  >
                    {b.label}: {b.count}
                  </button>
                ))}
                {filter && (
                  <button type="button" data-testid="pfilter-clear" onClick={() => setFilter(null)}
                    className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600 hover:bg-slate-200">Show all</button>
                )}
              </div>
              <div className="mt-3 max-h-72 overflow-y-auto rounded-lg border border-slate-100">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500">
                    <tr><th className="px-3 py-2">Brand</th><th className="px-3 py-2">Product Name</th><th className="px-3 py-2">Status</th></tr>
                  </thead>
                  <tbody data-testid="import-products-preview-table">
                    {visibleRows.length === 0 ? (
                      <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-400">No rows for this filter.</td></tr>
                    ) : visibleRows.map((r, i) => (
                      <tr key={i} className="border-t border-slate-50">
                        <td className="px-3 py-2 text-slate-700">{r.brand || <span className="text-slate-400">—</span>}</td>
                        <td className="px-3 py-2 text-slate-600">{r.name || <span className="text-slate-400">—</span>}</td>
                        <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_BADGE[r.status].cls}`}>{STATUS_BADGE[r.status].label}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {rows && !summary && (
          <div className="flex items-center justify-between gap-2 border-t border-slate-100 p-5">
            {importing && progress ? (
              <p data-testid="import-products-progress" className="text-sm font-semibold text-blue-700">Importing… {progress.done}/{progress.total} imported</p>
            ) : (
              <p className="text-xs text-slate-500"><b>{counts.ready || 0}</b> product(s) will be created (blank prices/images/offers).</p>
            )}
            <div className="flex gap-2">
              <button onClick={onClose} disabled={importing} className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
              <button data-testid="confirm-products-import-button" onClick={confirmImport} disabled={importing || !(counts.ready > 0)}
                className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
                {importing ? `Importing ${progress ? `${progress.done}/${progress.total}` : ""}` : `Confirm Import (${counts.ready || 0})`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
